<template>
  <div class="max-w-4xl mx-auto">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">My Profile</h1>
            <p class="text-orange-100 text-lg">Manage your account settings</p>
          </div>
          <div class="w-16 h-16 bg-white bg-opacity-20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile Information -->
    <div class="bg-white shadow-lg rounded-xl border border-gray-200 p-6 mb-8">
      <h2 class="text-xl font-bold text-gray-900 mb-6">Profile Information</h2>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
          <div class="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
            {{ userStore.user?.name || 'Not provided' }}
          </div>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <div class="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
            {{ userStore.user?.email || 'Not provided' }}
          </div>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Phone</label>
          <div class="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
            {{ userStore.user?.phone || 'Not provided' }}
          </div>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Role</label>
          <div class="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
            <span class="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
              {{ formatRole(userStore.user?.role) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Password Change Section -->
    <div class="bg-white shadow-lg rounded-xl border border-gray-200 p-6">
      <h2 class="text-xl font-bold text-gray-900 mb-6">Change Password</h2>
      
      <form @submit.prevent="changePassword" class="space-y-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
          <div class="relative">
            <input
              v-model="passwordForm.currentPassword"
              :type="showCurrentPassword ? 'text' : 'password'"
              required
              class="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
              :class="{ 'border-red-500': errors.currentPassword }"
              placeholder="Enter your current password"
            />
            <button
              type="button"
              @click="showCurrentPassword = !showCurrentPassword"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Toggle current password visibility"
            >
              <svg v-if="showCurrentPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.758 7.758M12 12l2.122 2.122m-2.122-2.122L7.758 7.758m6.364 6.364L12 12m2.122 2.122l2.122 2.122" />
              </svg>
              <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>
          <p v-if="errors.currentPassword" class="mt-1 text-sm text-red-600">{{ errors.currentPassword }}</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">New Password</label>
          <div class="relative">
            <input
              v-model="passwordForm.newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              required
              class="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
              :class="{ 'border-red-500': errors.newPassword }"
              placeholder="Enter your new password"
            />
            <button
              type="button"
              @click="showNewPassword = !showNewPassword"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Toggle new password visibility"
            >
              <svg v-if="showNewPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.758 7.758M12 12l2.122 2.122m-2.122-2.122L7.758 7.758m6.364 6.364L12 12m2.122 2.122l2.122 2.122" />
              </svg>
              <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>
          <p v-if="errors.newPassword" class="mt-1 text-sm text-red-600">{{ errors.newPassword }}</p>
          <p class="mt-1 text-xs text-gray-500">Password must be at least 6 characters long</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
          <div class="relative">
            <input
              v-model="passwordForm.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              required
              class="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
              :class="{ 'border-red-500': errors.confirmPassword }"
              placeholder="Confirm your new password"
            />
            <button
              type="button"
              @click="showConfirmPassword = !showConfirmPassword"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Toggle confirm password visibility"
            >
              <svg v-if="showConfirmPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.758 7.758M12 12l2.122 2.122m-2.122-2.122L7.758 7.758m6.364 6.364L12 12m2.122 2.122l2.122 2.122" />
              </svg>
              <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>
          <p v-if="errors.confirmPassword" class="mt-1 text-sm text-red-600">{{ errors.confirmPassword }}</p>
        </div>

        <div class="flex justify-end space-x-3 pt-6">
          <button
            type="button"
            @click="resetForm"
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="changingPassword"
            class="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
          >
            {{ changingPassword ? 'Changing Password...' : 'Change Password' }}
          </button>
        </div>
      </form>

      <!-- Success Message -->
      <div v-if="successMessage" class="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <div class="flex items-start">
          <svg class="h-5 w-5 text-green-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p class="text-sm font-medium text-green-800">{{ successMessage }}</p>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex items-start">
          <svg class="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p class="text-sm font-medium text-red-800">{{ errorMessage }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useUserStore } from '../stores/user'
import { authAPI } from '../services/api'
import { useNotificationStore } from '../stores/notification'

export default {
  name: 'Profile',
  setup() {
    const userStore = useUserStore()
    const notificationStore = useNotificationStore()
    
    // Form data
    const passwordForm = ref({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    
    // Form state
    const changingPassword = ref(false)
    const showCurrentPassword = ref(false)
    const showNewPassword = ref(false)
    const showConfirmPassword = ref(false)
    
    // Messages
    const successMessage = ref('')
    const errorMessage = ref('')
    
    // Form errors
    const errors = ref({})

    const formatRole = (role) => {
      const roleMap = {
        customer: 'Customer',
        workshop: 'Workshop',
        admin: 'Admin',
        staff: 'Staff'
      }
      return roleMap[role] || role
    }

    const validateForm = () => {
      errors.value = {}
      
      if (!passwordForm.value.currentPassword) {
        errors.value.currentPassword = 'Current password is required'
      }
      
      if (!passwordForm.value.newPassword) {
        errors.value.newPassword = 'New password is required'
      } else if (passwordForm.value.newPassword.length < 6) {
        errors.value.newPassword = 'New password must be at least 6 characters'
      }
      
      if (!passwordForm.value.confirmPassword) {
        errors.value.confirmPassword = 'Please confirm your new password'
      } else if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
        errors.value.confirmPassword = 'Passwords do not match'
      }
      
      return Object.keys(errors.value).length === 0
    }

    const changePassword = async () => {
      if (!validateForm()) return
      
      changingPassword.value = true
      successMessage.value = ''
      errorMessage.value = ''
      
      try {
        await authAPI.changePassword(
          passwordForm.value.currentPassword,
          passwordForm.value.newPassword
        )
        
        successMessage.value = 'Password changed successfully!'
        resetForm()
        notificationStore.showSuccess('Password Changed', 'Your password has been updated successfully')
        
      } catch (error) {
        console.error('Password change error:', error)
        
        const errorData = error.response?.data
        if (errorData?.error === 'InvalidCurrentPassword') {
          errors.value.currentPassword = 'Current password is incorrect'
        } else if (errorData?.error === 'NoPasswordSet') {
          errorMessage.value = 'No password is set for this account. Please contact administrator.'
        } else {
          errorMessage.value = errorData?.message || 'Failed to change password. Please try again.'
        }
        
        notificationStore.showError('Password Change Failed', errorMessage.value)
      } finally {
        changingPassword.value = false
      }
    }

    const resetForm = () => {
      passwordForm.value = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      errors.value = {}
      successMessage.value = ''
      errorMessage.value = ''
    }

    onMounted(() => {
      // Clear any existing messages when component mounts
      successMessage.value = ''
      errorMessage.value = ''
    })

    return {
      userStore,
      passwordForm,
      changingPassword,
      showCurrentPassword,
      showNewPassword,
      showConfirmPassword,
      successMessage,
      errorMessage,
      errors,
      formatRole,
      changePassword,
      resetForm
    }
  }
}
</script>
